// src/pages/GroupedApplicationsExternalExpert.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from '../api/axios';
import './GroupedApplicationsExternalExpert.css';

/* ===== helpers for deadline ===== */
const safeDate = (s) => (s ? new Date(String(s).replace(' ', 'T')) : null);

const formatTimeLeft = (deadlineAt, t, locale = 'kz') => {
  if (!deadlineAt) return '—';
  const d = safeDate(deadlineAt);
  if (!d) return '—';
  const now = new Date();
  const diffMs = d - now;
  if (diffMs <= 0) return t.overdue;

  const totalMin = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;

  const L = locale === 'ru'
    ? { d: 'д', h: 'ч', m: 'м' }
    : { d: 'к', h: 'сағ', m: 'мин' };

  if (days > 0) return `${days}${L.d} ${hours}${L.h} ${mins}${L.m}`;
  if (hours > 0) return `${hours}${L.h} ${mins}${L.m}`;
  return `${mins}${L.m}`;
};

const minutesUntil = (deadlineAt) => {
  if (!deadlineAt) return null;
  const d = safeDate(deadlineAt);
  if (!d) return null;
  return Math.floor((d - new Date()) / 60000);
};

const getTimeSeverity = (deadlineAt) => {
  const mins = minutesUntil(deadlineAt);
  if (mins === null) return 'none';
  if (mins <= 0) return 'danger';
  if (mins <= 60) return 'danger';
  if (mins <= 24 * 60) return 'warn';
  return 'ok';
};

const summarizeGroupDeadline = (group) => {
  const apps = Array.isArray(group?.applications) ? group.applications : [];
  let nearest = null;
  let anyOverdue = false;

  apps.forEach((a) => {
    const dl = a?.deadline;
    if (!dl || !dl.deadline_at) return;
    const ts = safeDate(dl.deadline_at)?.getTime() ?? Infinity;
    const nts = safeDate(nearest?.deadline_at)?.getTime() ?? Infinity;
    if (!nearest || ts < nts) nearest = dl;
    if (dl.is_overdue === true) anyOverdue = true;
  });

  if (!nearest) return null;
  const nowOverdue = (safeDate(nearest.deadline_at) ?? new Date(0)) < new Date();

  return {
    start_at: nearest.start_at ?? null,
    deadline_at: nearest.deadline_at ?? null,
    is_overdue: anyOverdue || nowOverdue,
    minutes_left: nearest.minutes_left ?? null,
  };
};

/* ===== statuses: нормализация + локализация ===== */
const r = (re) => new RegExp(re, 'i');

/* === FIX: стабильная нормализация и алиасы === */
/* 1) Алиасы: расширим accepted_waiting */
const STATUS_ALIASES = {
  new: [
    /^\s*жаңа\s*өтініш\s*$/i,
    /^\s*нов(ая|ый)?\s*заяв\w*\s*$/i,
    /^new$/i,
  ],
  pending: [/күту|күтуде/i, /ожид/i, /pending/i],
  in_review: [/сарапт/i, /на\s+экспертиз/i, /in\s*review|review/i],
  accepted_waiting: [
    /қабылданған.*(күту|күтуде)/i,           // ✅ Қабылданған (күту)
    /принят[ао]?.*\(?.*ожид/i,               // Принята (ожидание)
  ],
  accepted: [/^қабылданды$/i, /^принят[ао]?$/i, /accepted/i],
  returned: [/қайтарыл/i, /возвращ/i, /returned/i],
  declined: [/бас\s*тарт/i, /отклон/i, /(declined|rejected)/i],
  draft: [/жоба/i, /черновик/i, /draft/i],
};

/* 2) Нормализация: вариант со скобками и без */
function normalizeRawStatusBase(raw) {
  return String(raw ?? '')
    .normalize('NFKC')
    .replace(/[«»"'`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
function stripParens(s) {
  return s.replace(/\(.*?\)/g, '').trim();
}

/* 3) Ключ статуса: сначала матчим ПОЛНУЮ строку (со скобками),
      отдельно ловим "accepted_waiting", потом пробуем без скобок */
function statusKeyFromRaw(raw) {
  const withParens = normalizeRawStatusBase(raw);
  const noParens   = stripParens(withParens);
  const lowFull    = withParens.toLowerCase();

  // Явная проверка на "Принята/Қабылданған (ожид/күту)"
  if ((/қабылданған/i.test(withParens) && /(күту|күтуде)/i.test(withParens)) ||
      (/принят[ао]?/i.test(withParens) && /ожид/i.test(withParens))) {
    return 'accepted_waiting';
  }

  // Пытаемся по алиасам — сначала полная строка
  for (const [key, list] of Object.entries(STATUS_ALIASES)) {
    if (list.some((re) => re.test(withParens))) return key;
  }
  // затем без скобок (на случай экзотики)
  for (const [key, list] of Object.entries(STATUS_ALIASES)) {
    if (list.some((re) => re.test(noParens))) return key;
  }

  // быстрые эвристики для "Новая заявка"
  if (lowFull.includes('жаңа') && lowFull.includes('өтініш')) return 'new';
  if (lowFull.includes('нов') && lowFull.includes('заяв')) return 'new';

  return 'unknown';
}



function statusClassByKey(key) {
  switch (key) {
    case 'new': return 'st-new';
    case 'pending': return 'st-pending';
    case 'in_review': return 'st-review';
    case 'accepted_waiting': return 'st-accepted-wait';
    case 'accepted': return 'st-accepted';
    case 'returned': return 'st-returned';
    case 'declined': return 'st-declined';
    case 'draft': return 'st-draft';
    default: return 'st-unknown';
  }
}

/* ===== main component ===== */
export default function GroupedApplicationsExternalExpert({ locale = 'kz' }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const [, forceMinuteTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceMinuteTick((x) => x + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const token = localStorage.getItem('token');

  const langHeader = { 'Accept-Language': locale === 'kz' ? 'kk' : 'ru' };


  const t = useMemo(
    () =>
      ({
        kz: {
          title: 'ОӘК',
          accept: 'Қабылдау',
          decline: 'Бас тарту',
          view: 'Толық көру',
          noApplications: 'ОӘК табылмады',
          close: 'Жабу',
          acceptedSuccess: 'ОӘК сәтті қабылданды!',
          declinedSuccess: 'ОӘК-ден бас тартылды!',
          acceptAll: 'Барлығын қабылдау',
          declineAll: 'Барлығынан бас тарту',
          confirmAccept: 'Сіз осы ОӘК қабылдайтыныңызға сенімдісіз бе?',
          confirmAcceptAll: 'Барлық ОӘК қабылдайтыныңызға сенімдісіз бе?',
          confirmDeclineAll: 'Барлық ОӘК-ден бас тартатыныңызға сенімдісіз бе?',
          acceptAllSuccess: 'Барлық ОӘК сәтті қабылданды!',
          errorOccurred: 'Қате орын алды.',
          publisher: 'Баспа',
          authors: 'Авторлар',
          subject: 'Пән',
          grade: 'Сынып',
          category: 'Санат',
          status: 'Күйі',
          sentAt: 'Жіберілген уақыты',
          loading: 'Жүктелуде...',
          titleColumn: 'Оқу басылымы',
          actions: 'Әрекет',
          expandAll: 'Барлығын ашу',
          collapseAll: 'Барлығын жабу',
          start: 'Басталуы',
          deadline: 'Дедлайн',
          overdue: 'Мерзімі өтті',
          noDeadline: 'Дедлайн жоқ',
          appsCount: 'ОӘК',
          statuses: {
            new: 'Жаңа ОӘК',
            pending: 'Күту',
            in_review: 'Сараптамада',
            accepted_waiting: 'Қабылданған (күту)',
            accepted: 'Қабылданды',
            returned: 'Қайтарылды',
            declined: 'Бас тартылды',
            draft: 'Жоба',
            unknown: 'Белгісіз'
          },
        },
        ru: {
          title: 'Заявки',
          accept: 'Принять',
          decline: 'Отклонить',
          view: 'Просмотр',
          noApplications: 'УМК не найдено',
          close: 'Закрыть',
          acceptedSuccess: 'УМК успешно принята!',
          declinedSuccess: 'УМК отклонено!',
          acceptAll: 'Принять все',
          declineAll: 'Отклонить все',
          confirmAccept: 'Вы уверены, что хотите принять эту УМК?',
          confirmAcceptAll: 'Вы уверены, что хотите принять все УМК?',
          confirmDeclineAll: 'Вы уверены, что хотите отклонить все УМК?',
          acceptAllSuccess: 'Все заявки успешно приняты!',
          errorOccurred: 'Произошла ошибка.',
          publisher: 'Издательство',
          authors: 'Авторы',
          subject: 'Предмет',
          grade: 'Класс',
          status: 'Статус',
          category: 'Категория',
          sentAt: 'Дата отправки',
          loading: 'Загрузка...',
          titleColumn: 'Учебное издание',
          actions: 'Действие',
          expandAll: 'Развернуть все',
          collapseAll: 'Свернуть все',
          start: 'Начало',
          deadline: 'Дедлайн',
          overdue: 'Просрочено',
          noDeadline: 'Нет дедлайна',
          appsCount: 'Заявок',
          statuses: {
            new: 'Новая заявка',
            pending: 'В ожидании',
            in_review: 'На экспертизе',
            accepted_waiting: 'Принята (ожидание)',
            accepted: 'Принята',
            returned: 'Возвращена',
            declined: 'Отклонена',
            draft: 'Черновик',
            unknown: 'Неизвестно'
          },
        },
      }[locale] || {}),
    [locale]
  );

  // удобная функция вывода локализованного статуса
  const getStatusLabel = useCallback(
    (raw) => t.statuses[statusKeyFromRaw(raw)] ?? (raw || '—'),
    [t]
  );

  // если хочешь использовать ключи в логике — можно так:
  const canAct = (app) => {
    const sKey = statusKeyFromRaw(app?.status);
    const meKey = statusKeyFromRaw(app?.status_me);
    if (sKey === 'accepted' || sKey === 'accepted_waiting' || meKey === 'accepted' || meKey === 'accepted_waiting') {
      return false;
    }
    return ['new', 'pending', 'draft'].some(k => k === sKey || k === meKey);
  };

  /* ===== аккордеон ===== */
  const [expanded, setExpanded] = useState({});
  const anyOpen = useMemo(() => groups.some((g) => expanded[g.id]), [groups, expanded]);
  const toggleGroup = useCallback((id) => setExpanded((p) => ({ ...p, [id]: !p[id] })), []);
  const toggleAllGroups = useCallback(() => {
    setExpanded((p) => {
      const next = {};
      const target = !anyOpen;
      groups.forEach((g) => (next[g.id] = target));
      return next;
    });
  }, [anyOpen, groups]);

  /* ===== загрузка данных ===== */
useEffect(() => {
   fetchApplications();
 }, [locale]); // 👈 язык изменился — заново грузим

 useEffect(() => {
  if (selectedApplication) {
    fetchApplicationDetails(selectedApplication.id);
  }
}, [locale]); // 👈


  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/expertise/external/applications/new', {
      headers: { Authorization: `Bearer ${token}`, ...langHeader },
    });

      const { data = [] } = res.data || {};
      const normalized = data
        .map((group) => {
          const apps = Array.isArray(group.applications) ? group.applications : [];
          return {
            id: group.id,
            title: group.title,
            publisher_name: group.publisher_name,
            applications: apps,
          };
        })
        .filter((g) => g.applications.length > 0);

      setGroups(normalized);
      setExpanded((prev) => {
        const next = {};
        normalized.forEach((g) => (next[g.id] = prev[g.id] ?? false));
        return next;
      });
    } catch (e) {

    } finally {
      setLoading(false);
    }
  };

  /* ===== действия ===== */
  const fetchApplicationDetails = async (id) => {
    try {
      const res = await axios.get(`/expertise/external/applications/${id}`, {
      headers: { Authorization: `Bearer ${token}`, ...langHeader },
    });
      setSelectedApplication(res?.data?.data ?? res.data ?? null);
    } catch (e) {

    }
  };

  const handleAcceptApplication = async (id) => {
    if (!window.confirm(t.confirmAccept)) return;
    try {
      await axios.post(`/expertise/external/applications/${id}/accept`, {}, { headers: { Authorization: `Bearer ${token}`, ...langHeader } });
      alert(t.acceptedSuccess);
      fetchApplications();
      setSelectedApplication(null);
    } catch {
      alert(t.errorOccurred);
    }
  };

  const handleAcceptAllApplications = async (group) => {
    if (!window.confirm(t.confirmAcceptAll)) return;
    try {
      const pending = group.applications.filter(canAct);
      if (!pending.length) return;
      await Promise.all(pending.map((app) =>
        axios.post(`/expertise/external/applications/${app.id}/accept`, {}, { headers: { Authorization: `Bearer ${token}`, ...langHeader } })
      ));
      alert(t.acceptAllSuccess);
      fetchApplications();
    } catch {
      alert(t.errorOccurred);
    }
  };

  const handleDeclineAllApplications = async (groupId) => {
    if (!window.confirm(t.confirmDeclineAll)) return;
    try {
      await axios.post(`/expertise/external/applications/${groupId}/decline`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert(t.declinedSuccess);
      fetchApplications();
    } catch {
      alert(t.errorOccurred);
    }
  };

  /* ===== render ===== */
  if (loading) return <p className="gai-muted">{t.loading}</p>;
  if (!groups.length) return <p className="gai-muted">{t.noApplications}</p>;

  return (
    <div className="gai-root">
      <div className="gai-toolbar">
        <button className="gai-btn" onClick={toggleAllGroups}>
          {anyOpen ? t.collapseAll : t.expandAll}
        </button>
      </div>

      {groups.map((group) => {
        const isOpen = expanded[group.id] ?? false;
        const summary = summarizeGroupDeadline(group);
        const timeText = summary ? formatTimeLeft(summary.deadline_at, t, locale) : '—';
        const severity = getTimeSeverity(summary?.deadline_at);
        const hasPending = group.applications.some(canAct);

        return (
          <div key={group.id} className={`gai-group-section ${isOpen ? 'is-open' : 'is-closed'}`}>
            <div className="gai-head" role="button" tabIndex={0} onClick={() => toggleGroup(group.id)}>
              <span className="gai-chev">{isOpen ? '▾' : '▸'}</span>
              <span className="gai-group-title">{group.title}</span>
              {group.publisher_name && <span className="gai-group-publisher"> — {group.publisher_name}</span>}
              <span className="gai-group-count">{t.appsCount}: {group.applications.length}</span>

              <span className={`gai-deadline ${severity} ${summary?.is_overdue ? 'overdue' : ''}`} style={{ marginLeft: 8 }}>
                {summary ? `${locale === 'ru' ? 'Оставшееся время: ' : 'Қалған уақыт: '}${timeText}` : t.noDeadline}
              </span>

              <div className="gai-head__spacer" />

              {hasPending && (
                <div className="gai-row-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="gai-btn-primary" onClick={() => handleAcceptAllApplications(group)}>{t.acceptAll}</button>
                  <button className="gai-btn-secondary" onClick={() => handleDeclineAllApplications(group.id)}>{t.declineAll}</button>
                </div>
              )}
            </div>

            {isOpen && (
              <div className="gai-group-panel">
                <table className="gai-table">
                  <thead>
                    <tr>
                     <th>{t.category}</th>
                      <th>{t.status}</th>
                      <th>{t.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.applications.map((app) => {
                      const sKey = statusKeyFromRaw(app.status);
                      return (
                        <tr key={app.id}>
                           <td>{app.category || '—'}</td>
                          <td>
                            <span className={`gai-badge ${statusClassByKey(sKey)}`}>
                              {getStatusLabel(app.status)}
                            </span>
                          </td>
                          <td>
                            <button
                              className="gai-btn"
                              onClick={(e) => { e.stopPropagation(); fetchApplicationDetails(app.id); }}
                            >
                              {t.view}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {selectedApplication && (
        <div className="gai-modal-overlay">
          <div className="gai-modal-inner">
            <div className="gai-modal-header">
              <h3>{selectedApplication.title}</h3>
              <button onClick={() => setSelectedApplication(null)} className="gai-btn">×</button>
            </div>
            <div className="gai-modal-body">
              <div className="gai-detail-grid">
                <div className="gai-detail-label">{t.publisher}</div>
                <div className="gai-detail-value">{selectedApplication.publisher || '—'}</div>


                <div className="gai-detail-label">{t.subject}</div>
                <div className="gai-detail-value">{selectedApplication.subject || '—'}</div>

                <div className="gai-detail-label">{t.grade}</div>
                <div className="gai-detail-value">{selectedApplication.grade || '—'}</div>

                <div className="gai-detail-label">{t.status}</div>
                <div className="gai-detail-value">
                  <span className={`gai-badge ${statusClassByKey(statusKeyFromRaw(selectedApplication.status))}`}>
                    {getStatusLabel(selectedApplication.status)}
                  </span>
                </div>

                <div className="gai-detail-label">{t.sentAt}</div>
                <div className="gai-detail-value">{selectedApplication.sent_at || '—'}</div>
              </div>

              {canAct(selectedApplication) && (
                <div className="gai-detail-actions">
                  <button className="gai-btn-primary" onClick={() => handleAcceptApplication(selectedApplication.id)}>{t.accept}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
